/**
 * Emits the tokens as `--j-*` custom properties, once, through the theme's CssBaseline.
 * Colours also get `--j-<role>-rgb` channels for `rgb(var(--j-ink-rgb) / 0.24)`.
 */ import { colour, darkColour, darkTags, density, elevation, focus, fontFamily, motion, radius, ROOT_PX, size, space, tags, type } from "./tokens";
/** `accentHover` → `accent-hover`, `ink2` → `ink-2`, `btnHLg` → `btn-h-lg`. */ const toKebab = (name)=>name.replace(/([a-z])([A-Z0-9])/g, "$1-$2").replace(/([A-Z])([A-Z])/g, "$1-$2").toLowerCase();
const hexToRgbChannels = (hex)=>[
        1,
        3,
        5
    ].map((offset)=>parseInt(hex.slice(offset, offset + 2), 16)).join(" ");
const prefixed = (group, values)=>Object.fromEntries(Object.entries(values).map(([key, value])=>[
            `--j-${group}${toKebab(key)}`,
            value
        ]));
const colourDeclarations = (roles, tagPairs)=>{
    const colours = {
        ...prefixed("", roles),
        ...prefixed("", Object.fromEntries(Object.entries(tagPairs).flatMap(([name, pair])=>[
                [
                    `tag-${name}-bg`,
                    pair.bg
                ],
                [
                    `tag-${name}-fg`,
                    pair.fg
                ]
            ])))
    };
    return {
        ...colours,
        ...Object.fromEntries(Object.entries(colours).map(([name, hex])=>[
                `${name}-rgb`,
                hexToRgbChannels(hex)
            ]))
    };
};
const declarations = {
    "--j-font": fontFamily,
    "color-scheme": "light",
    ...colourDeclarations(colour, tags),
    ...Object.fromEntries(Object.entries(type).flatMap(([step, value])=>[
            [
                `--j-fs-${step}`,
                value.size
            ],
            [
                `--j-lh-${step}`,
                value.lineHeight
            ]
        ])),
    ...prefixed("sp-", space),
    ...prefixed("r-", radius),
    ...prefixed("", size),
    ...prefixed("", density),
    ...Object.fromEntries(Object.entries(elevation).map(([name, value])=>[
            `--j-${name}`,
            value
        ])),
    ...prefixed("focus-", focus),
    ...prefixed("", motion),
    "font-size": `${ROOT_PX / 16 * 100}%`
};
const block = (selector, rules)=>`${selector}{${Object.entries(rules).map(([name, value])=>`${name}:${value}`).join(";")}}`;
export const tokenCss = [
    block(":root", declarations),
    block(":root[data-theme=dark]", {
        "color-scheme": "dark",
        ...colourDeclarations(darkColour, darkTags)
    }),
    block("body", {
        "font-family": "var(--j-font)",
        "font-size": "var(--j-fs-md)",
        "line-height": "var(--j-lh-md)",
        color: "var(--j-ink)",
        "background-color": "var(--j-surface)",
        "-webkit-font-smoothing": "antialiased"
    }),
    block("button, input, textarea, select", {
        "font-family": "inherit"
    }),
    block(":focus-visible", {
        outline: "var(--j-focus-w) solid var(--j-accent)",
        "outline-offset": "var(--j-focus-offset)"
    })
].join("\n");
